class CartAutoAdd {
  constructor() {
    this.addOnRules = this.getAddOnRules();
    this.cartUpdateUnsubscriber = undefined;
    this.init();
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  init() {
    // Subscribe to cart updates
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.handleCartUpdate(event);
    });

    // Initial check on page load
    this.checkAddOnRules();
  }

  getAddOnRules() {
    // Look for cart drawer section or cart drawer elements
    const cartDrawerSection = document.querySelector('[data-section-type="cart-drawer"]') || 
                              document.querySelector('cart-drawer') ||
                              document.querySelector('#CartDrawer');
    
    if (!cartDrawerSection) {
      console.log('CartAutoAdd: No cart drawer section found');
      return [];
    }

    const addOnBlocks = cartDrawerSection.querySelectorAll('[data-block-type="add_ons"]');
    const totalThresholdBlocks = cartDrawerSection.querySelectorAll('[data-block-type="total_threshold"]');
    
    console.log(`CartAutoAdd: Found ${addOnBlocks.length} add-on blocks and ${totalThresholdBlocks.length} total threshold blocks`);
    
    const rules = [];

    // Process add-on blocks
    addOnBlocks.forEach((block, index) => {
      const targetProductId = block.dataset.targetProductId;
      const payloadProductId = block.dataset.payloadProductId;
      const thresholdQuantity = parseInt(block.dataset.thresholdQuantity) || 1;
      const payloadQuantity = parseInt(block.dataset.payloadQuantity) || 1;
      const removeWhenBelowThreshold = block.dataset.removeWhenBelowThreshold === 'true';

      console.log(`CartAutoAdd: Add-on Block ${index + 1}:`, {
        targetProductId,
        payloadProductId,
        thresholdQuantity,
        payloadQuantity,
        removeWhenBelowThreshold
      });

      if (targetProductId && payloadProductId) {
        rules.push({
          type: 'add_on',
          targetProductId: parseInt(targetProductId),
          payloadProductId: parseInt(payloadProductId),
          thresholdQuantity,
          payloadQuantity,
          removeWhenBelowThreshold
        });
      }
    });

    // Process total threshold blocks
    totalThresholdBlocks.forEach((block, index) => {
      const thresholdAmount = parseFloat(block.dataset.thresholdAmount) || 0;
      const payloadProductId = block.dataset.payloadProductId;
      const payloadQuantity = parseInt(block.dataset.payloadQuantity) || 1;
      const removeWhenBelowThreshold = block.dataset.removeWhenBelowThreshold === 'true';
      const thresholdType = block.dataset.thresholdType || 'above';

      console.log(`CartAutoAdd: Total Threshold Block ${index + 1}:`, {
        thresholdAmount,
        payloadProductId,
        payloadQuantity,
        removeWhenBelowThreshold,
        thresholdType
      });

      if (payloadProductId) {
        rules.push({
          type: 'total_threshold',
          thresholdAmount,
          payloadProductId: parseInt(payloadProductId),
          payloadQuantity,
          removeWhenBelowThreshold,
          thresholdType
        });
      }
    });

    console.log(`CartAutoAdd: Created ${rules.length} rules`);
    return rules;
  }

  async handleCartUpdate(event) {
    // Small delay to ensure cart data is updated
    setTimeout(() => {
      this.checkAddOnRules();
    }, 100);
  }

  refreshRules() {
    this.addOnRules = this.getAddOnRules();
  }

  async checkAddOnRules() {
    if (this.addOnRules.length === 0) return;

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cartData = await cartResponse.json();

      for (const rule of this.addOnRules) {
        await this.processAddOnRule(rule, cartData);
      }
    } catch (error) {
      console.error('Error checking add-on rules:', error);
    }
  }

  async processAddOnRule(rule, cartData) {
    if (rule.type === 'add_on') {
      await this.processAddOnRuleType(rule, cartData);
    } else if (rule.type === 'total_threshold') {
      await this.processTotalThresholdRule(rule, cartData);
    }
  }

  async processAddOnRuleType(rule, cartData) {
    console.log('CartAutoAdd: Cart items:', cartData.items.map(item => ({ 
      variant_id: item.variant_id, 
      id: item.id, 
      quantity: item.quantity, 
      index: item.index,
      key: item.key
    })));
    console.log('CartAutoAdd: Looking for target:', rule.targetProductId, 'payload:', rule.payloadProductId);
    
    const targetItem = cartData.items.find(item => item.variant_id == rule.targetProductId);
    const payloadItem = cartData.items.find(item => item.variant_id == rule.payloadProductId);
    const payloadItemIndex = cartData.items.findIndex(item => item.variant_id == rule.payloadProductId);

    const targetQuantity = targetItem ? targetItem.quantity : 0;
    const payloadQuantity = payloadItem ? payloadItem.quantity : 0;
    const payloadLine = payloadItem ? payloadItemIndex + 1 : null;

    console.log(`CartAutoAdd: Target quantity: ${targetQuantity}, Payload quantity: ${payloadQuantity}, Payload line: ${payloadLine}`);

    // Check if we need to add the payload product
    if (targetQuantity >= rule.thresholdQuantity && payloadQuantity === 0) {
      console.log(`CartAutoAdd: Adding payload product (target: ${targetQuantity} >= threshold: ${rule.thresholdQuantity})`);
      await this.addPayloadProduct(rule);
    }
    // Check if we need to remove the payload product
    else if (targetQuantity < rule.thresholdQuantity && payloadQuantity > 0 && rule.removeWhenBelowThreshold) {
      console.log(`CartAutoAdd: Removing payload product (target: ${targetQuantity} < threshold: ${rule.thresholdQuantity})`);
      await this.removePayloadProduct(rule, payloadLine);
    }
    // Check if we need to adjust payload quantity based on target quantity
    else if (targetQuantity >= rule.thresholdQuantity && payloadQuantity > 0) {
      const expectedPayloadQuantity = Math.floor(targetQuantity / rule.thresholdQuantity) * rule.payloadQuantity;
      if (payloadQuantity !== expectedPayloadQuantity) {
        console.log(`CartAutoAdd: Updating payload quantity from ${payloadQuantity} to ${expectedPayloadQuantity}`);
        await this.updatePayloadQuantity(rule, expectedPayloadQuantity, payloadLine);
      }
    }
  }

  async processTotalThresholdRule(rule, cartData) {
    console.log('CartAutoAdd: Cart items for total threshold:', cartData.items.map(item => ({ 
      variant_id: item.variant_id, 
      id: item.id, 
      quantity: item.quantity, 
      index: item.index,
      key: item.key
    })));
    console.log('CartAutoAdd: Looking for payload:', rule.payloadProductId);

    console.log('CartAutoAdd: Cart data:', cartData);
    
    const payloadItem = cartData.items.find(item => item.variant_id == rule.payloadProductId);
    const payloadItemIndex = cartData.items.findIndex(item => item.variant_id == rule.payloadProductId); 

    console.log('CartAutoAdd: Payload item:', payloadItem);
    console.log('CartAutoAdd: Payload item index:', payloadItemIndex);

    const payloadQuantity = payloadItem ? payloadItem.quantity : 0;
    const payloadLine = payloadItem ? payloadItemIndex + 1 : null;
    
    // Calculate cart subtotal (excluding the payload product to avoid circular logic)
    const cartSubtotal = cartData.items
      .filter(item => item.variant_id != rule.payloadProductId)
      .reduce((total, item) => total + (item.final_line_price / 100), 0);

    console.log(`CartAutoAdd: Cart subtotal: $${cartSubtotal}, Threshold: $${rule.thresholdAmount}, Type: ${rule.thresholdType}, Payload quantity: ${payloadQuantity}, Payload line: ${payloadLine}`);

    let shouldHavePayload = false;
    
    if (rule.thresholdType === 'above') {
      shouldHavePayload = cartSubtotal >= rule.thresholdAmount;
    } else if (rule.thresholdType === 'below') {
      shouldHavePayload = cartSubtotal < rule.thresholdAmount;
    }

    // Check if we need to add the payload product
    if (shouldHavePayload && payloadQuantity === 0) {
      console.log(`CartAutoAdd: Adding payload product (subtotal: $${cartSubtotal} ${rule.thresholdType} threshold: $${rule.thresholdAmount})`);
      await this.addPayloadProduct(rule);
    }
    // Check if we need to remove the payload product
    else if (!shouldHavePayload && payloadQuantity > 0 && rule.removeWhenBelowThreshold) {
      console.log(`CartAutoAdd: Removing payload product (subtotal: $${cartSubtotal} not ${rule.thresholdType} threshold: $${rule.thresholdAmount})`);
      await this.removePayloadProduct(rule, payloadLine);
    }
    // Check if we need to adjust payload quantity
    else if (shouldHavePayload && payloadQuantity > 0 && payloadQuantity !== rule.payloadQuantity) {
      console.log(`CartAutoAdd: Updating payload quantity from ${payloadQuantity} to ${rule.payloadQuantity}`);
      await this.updatePayloadQuantity(rule, rule.payloadQuantity, payloadLine);
    }
  }

  async addPayloadProduct(rule) {
    const body = JSON.stringify({
      items: [{
        id: rule.payloadProductId,
        quantity: rule.payloadQuantity
      }]
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.status) {
          console.error('Error adding payload product:', data);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-auto-add',
            productVariantId: rule.payloadProductId,
            errors: data.errors || data.description,
            message: data.message,
          });
          return;
        }

        console.log(`Auto-added payload product ${rule.payloadProductId}`);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-auto-add', cartData: data });
      })
      .catch((e) => {
        console.error('Error adding payload product:', e);
      });
  }

  async removePayloadProduct(rule, line) {
    if (!line) {
      console.log('Payload product not found in cart (already removed or never added)');
      return;
    }

    // Use existing cart infrastructure
    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (cartItems) {
      cartItems.updateQuantity(line, 0, '', rule.payloadProductId);
      console.log(`Auto-removed payload product at line ${line}`);
    }
  }

  async updatePayloadQuantity(rule, newQuantity, line) {
    if (!line) {
      console.log('Payload product not found in cart (cannot update quantity)');
      return;
    }

    // Use existing cart infrastructure
    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (cartItems) {
      cartItems.updateQuantity(line, newQuantity, '', rule.payloadProductId);
      console.log(`Auto-updated payload product at line ${line} quantity to ${newQuantity}`);
    }
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

// Initialize cart auto-add when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartAutoAdd = new CartAutoAdd();
});

// Re-initialize when sections are loaded (for cart drawer)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('cart-drawer') || event.target.querySelector('.cart-drawer')) {
    if (window.cartAutoAdd) {
      window.cartAutoAdd.refreshRules();
    } else {
      window.cartAutoAdd = new CartAutoAdd();
    }
  }
}); 